import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import QRCode from 'qrcode'
import { getUpiIntent } from '../../services/apis/payments'
import { readAmountFromUpiIntent } from '../../util/upiPayment'

const UPI_APP_LABELS = ['PhonePe', 'GPay', 'Paytm', 'BHIM', 'WhatsApp'] as const

/**
 * Standalone hosted UPI payment page (Upstox/Setu-style).
 * Opened in a new tab after bank verification is initiated.
 * Intent is fetched by transaction id so the UPI string never appears in the URL.
 */
const UpiPaymentPage = () => {
    const { transactionId = '' } = useParams<{ transactionId: string }>()
    const [amount, setAmount] = useState('1.00')
    const [qrDataUrl, setQrDataUrl] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        if (!transactionId) {
            setError('Missing payment reference.')
            setLoading(false)
            return
        }

        let cancelled = false

        const load = async () => {
            setLoading(true)
            setError(null)
            try {
                const intent = await getUpiIntent(transactionId)
                const intentString = intent.upiIntentString
                let payAmount = intent.amount || readAmountFromUpiIntent(intentString) || '1.00'

                if (!intentString) {
                    throw new Error('UPI payment details are not available. Please go back and try again.')
                }

                const dataUrl = await QRCode.toDataURL(intentString, {
                    width: 280,
                    margin: 2,
                    errorCorrectionLevel: 'M',
                })

                if (cancelled) return
                setAmount(payAmount)
                setQrDataUrl(dataUrl)
            } catch (err: any) {
                if (!cancelled) {
                    setError(err?.response?.data?.message || err?.message || 'Failed to load payment QR.')
                }
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        void load()
        return () => {
            cancelled = true
        }
    }, [transactionId])

    return (
        <Box
            sx={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 2,
                background: 'linear-gradient(180deg, #e8f4fc 0%, #f7fbff 45%, #ffffff 100%)',
                overflow: 'auto',
            }}
        >
            <Box
                sx={{
                    width: '100%',
                    maxWidth: 420,
                    bgcolor: '#fff',
                    borderRadius: 3,
                    boxShadow: '0 12px 40px rgba(15, 23, 42, 0.08)',
                    px: { xs: 3, sm: 4 },
                    py: { xs: 4, sm: 5 },
                    textAlign: 'center',
                    my: 3,
                }}
            >
                <Typography fontSize="13px" color="text.secondary" letterSpacing={0.4} mb={1}>
                    Bank account verification
                </Typography>
                <Typography fontWeight={700} fontSize="32px" lineHeight={1.2} mb={0.5} color="text.primary">
                    Pay ₹{amount}
                </Typography>
                <Typography fontSize="14px" color="text.secondary" mb={3}>
                    Scan QR code with any UPI app
                </Typography>

                {loading && (
                    <Stack alignItems="center" spacing={2} py={6}>
                        <CircularProgress />
                        <Typography fontSize="13px" color="text.secondary">
                            Preparing payment…
                        </Typography>
                    </Stack>
                )}

                {!loading && error && (
                    <Stack spacing={2} py={2}>
                        <Typography fontSize="14px" color="error.main">
                            {error}
                        </Typography>
                        <Button
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                            onClick={() => window.location.reload()}
                        >
                            Retry
                        </Button>
                    </Stack>
                )}

                {!loading && !error && qrDataUrl && (
                    <>
                        <Stack direction="row" spacing={1.5} justifyContent="center" flexWrap="wrap" useFlexGap mb={2.5}>
                            {UPI_APP_LABELS.map((label) => (
                                <Box
                                    key={label}
                                    sx={{
                                        px: 1.25,
                                        py: 0.5,
                                        borderRadius: 5,
                                        bgcolor: 'action.hover',
                                        fontSize: '11px',
                                        fontWeight: 600,
                                        color: 'text.secondary',
                                    }}
                                >
                                    {label}
                                </Box>
                            ))}
                        </Stack>

                        <Box
                            component="img"
                            src={qrDataUrl}
                            alt="UPI payment QR code"
                            sx={{
                                width: 280,
                                height: 280,
                                mx: 'auto',
                                display: 'block',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: '#fff',
                            }}
                        />
                    </>
                )}
            </Box>
        </Box>
    )
}

export default UpiPaymentPage
